import React, { useState, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import CashPaymentModal from "../../../../utils/cashPaymentModal";
import WhtPaymentModal from "../../../../utils/whtPaymentModal";
import styles from "./invoice.module.css";
import {
    sendInvoiceToManager,
    resendInvoiceNotification,
    fetchPropertyManagersURL,
    voidInvoice,
} from "../../../../utils/urls";

const InvoiceActionsDropdown = ({
    invoice,
    facilityId,
    handleDownloadPDF,
    setShowReceiptsDialog,
    onCashPaymentSuccess,
    onCashPaymentError,
    onInvoiceVoided,
    loading
}) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
    const [showWhtPaymentModal, setShowWhtPaymentModal] = useState(false);
    const [showSendManagerDialog, setShowSendManagerDialog] = useState(false);
    const [managers, setManagers] = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [loadingManagers, setLoadingManagers] = useState(false);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [resending, setResending] = useState(false);
    const [voidingInvoice, setVoidingInvoice] = useState(false);
    const toast = useRef(null);

    // Debug state to show raw response data in UI
    const [debugResponse, setDebugResponse] = useState(null);

    const getAvailableActions = () => {
        const baseActions = [
            { label: "Receipts", value: "receipts", icon: "fas fa-receipt" },
            { label: "Download Invoice", value: "download", icon: "fas fa-download" }
        ];

        // Only add these actions if invoice is not null
        if (invoice) {
            baseActions.push({
                label: "Manual Payment",
                value: "cash-payment",
                icon: "fas fa-money-bill"
            });

            baseActions.push({
                label: "Record Wht",
                value: "wht-payment",
                icon: "fas fa-money-bill"
            });

            baseActions.push({
                label: "Send to Client",
                value: "resend",
                icon: "fas fa-paper-plane"
            });

            baseActions.push({
                label: "Send to Property Manager",
                value: "send-manager",
                icon: "fas fa-user-tie"
            });

            // Add void invoice action (only for non-voided invoices)
            if (invoice.status !== 'voided' && invoice.status !== 'VOIDED' &&
                invoice.status !== 'void' && invoice.status !== 'VOID') {
                baseActions.push({
                    label: "Void Invoice",
                    value: "void-invoice",
                    icon: "fas fa-ban"
                });
            }
        }

        return baseActions;
    };

    const actions = getAvailableActions();

    const handleActionChange = (e) => {
        const action = e.value;
        setSelectedAction(action);

        switch (action) {
            case "receipts":
                setShowReceiptsDialog(true);
                break;
            case "cash-payment":
                setShowCashPaymentModal(true);
                break;
            case "wht-payment":
                setShowWhtPaymentModal(true);
                break;
            case "resend":
                handleResendInvoice();
                break;
            case "download":
                handleDownloadPDF();
                break;
            case "send-manager":
                loadPropertyManagers();
                break;
            case "void-invoice":
                handleVoidInvoice();
                break;
            default:
                break;
        }

        setTimeout(() => setSelectedAction(null), 500);
    };

    const handleVoidInvoice = async () => {
        if (!invoice || !invoice._id) {
            toastify("Invalid invoice data", "error");
            return;
        }

        // Get current user data from storage
        let currentUser = null;
        try {
            // Try different storage keys that might contain user data
            const userId = await getItem('selectedCustomerId') || await getItem('userId') || await getItem('currentUserId');
            const userInfo = await getItem('currentUser') || await getItem('user') || await getItem('userInfo');

            if (userInfo) {
                try {
                    currentUser = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo;
                } catch (e) {
                    console.warn("Could not parse user info from storage");
                }
            }

            // If we don't have complete user info, create a basic structure
            if (!currentUser || !currentUser.userId) {
                currentUser = {
                    userId: userId || 'system',
                    fullName: 'System Admin',
                    role: 'Property Manager'
                };
            }

            console.log("🔍 DEBUG - Current user for void:", currentUser);
            console.log("🔍 DEBUG - Invoice ID:", invoice._id);
            console.log("🔍 DEBUG - Facility ID:", facilityId);

        } catch (error) {
            console.error("Error getting user data:", error);
            currentUser = {
                userId: 'system',
                fullName: 'System Admin',
                role: 'Property Manager'
            };
        }

        confirmDialog({
            message: "Are you sure you want to void this invoice? This action cannot be undone.",
            header: "Void Invoice Confirmation",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Yes, Void Invoice",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    setVoidingInvoice(true);
                    toastify("Voiding invoice...", "info");

                    const payload = {
                        reason: "Voided by property manager",
                        userId: currentUser.userId,
                        userName: currentUser.fullName || currentUser.firstName || currentUser.username || "System Admin",
                        userRole: currentUser.role || "Property Manager",
                    };

                    console.log("🔍 DEBUG - Void invoice payload:", payload);

                    const voidUrl = `${voidInvoice}/${facilityId}/${invoice._id}`;
                    console.log("🔍 DEBUG - Void URL:", voidUrl);

                    const response = await makeRequest2(voidUrl, "POST", payload);

                    console.log("🔍 DEBUG - Void response:", response);

                    if (response.success || response.data) {
                        toastify("Invoice successfully voided", "success");
                        // Call the callback to refresh the invoice data
                        if (typeof onInvoiceVoided === 'function') {
                            onInvoiceVoided();
                        }
                    } else {
                        const errorMessage = response.error || response.message || "Failed to void invoice";
                        console.error("🔍 DEBUG - Void failed:", errorMessage);
                        toastify(errorMessage, "error");
                    }
                } catch (error) {
                    console.error("🔍 DEBUG - Error voiding invoice:", error);
                    toastify("Failed to void invoice. Please try again.", "error");
                } finally {
                    setVoidingInvoice(false);
                }
            },
        });
    };

    const loadPropertyManagers = async () => {
        try {
            setLoadingManagers(true);
            setDebugResponse(null); // Reset debug info

            // Create the request URL for fetching property managers
            const requestUrl = `${fetchPropertyManagersURL}/${facilityId}`;

            const response = await makeRequest2(requestUrl, "GET");
            setDebugResponse(response); // Store for debugging in UI

            // Check if response is successful
            if (!response.success) {
                setManagers([]);
                setShowSendManagerDialog(true);
                toastify("Failed to fetch property managers: " + (response.message || "Unknown error"), "error");
                return;
            }

            // Determine how to process the data based on its structure
            let managersData = [];

            if (Array.isArray(response.data)) {
                // Direct array of managers
                managersData = response.data;
            } else if (response.data && typeof response.data === 'object') {
                // Check if there's a nested data property or other structure
                if (Array.isArray(response.data.data)) {
                    managersData = response.data.data;
                } else if (response.data.propertyManagers && Array.isArray(response.data.propertyManagers)) {
                    managersData = response.data.propertyManagers;
                } else {
                    // Look for any array property in the response
                    for (const key in response.data) {
                        if (Array.isArray(response.data[key])) {
                            console.log("🔍 DEBUG - Found array property:", key);
                            managersData = response.data[key];
                            break;
                        }
                    }
                }
            }

            if (managersData && managersData.length > 0) {
                // Process the managers data and add display labels
                const processedManagers = managersData.map(manager => ({
                    ...manager,
                    displayLabel: `${manager.fullName || manager.name || 'Unknown'} (${manager.type || manager.role || 'User'})`
                }));

                setManagers(processedManagers);
                setShowSendManagerDialog(true);
            } else {
                // No managers found
                setManagers([]);
                setShowSendManagerDialog(true);
                toastify("No property managers found for this facility", "warning");
            }
        } catch (error) {
            setManagers([]);
            setShowSendManagerDialog(true);
            toastify("Failed to fetch property managers: " + (error.message || "Unknown error"), "error");
        } finally {
            setLoadingManagers(false);
        }
    };

    const handleSendToManager = async () => {
        if (!selectedManager) {
            toastify("Please select a property manager", "warning");
            return;
        }

        try {
            setSendingInvoice(true);

            // Create the proper payload for sending the invoice
            const payload = {
                managerId: selectedManager._id,
                email: selectedManager.email, // Using 'email' instead of 'managerEmail'
                invoiceId: invoice._id, // Make sure invoiceId is included
                // Include additional fields that might be needed
                managerName: selectedManager.fullName,
                invoiceNumber: invoice.invoiceNumber,
                clientName: `${invoice.client?.firstName || ''} ${invoice.client?.lastName || ''}`,
                unitName: invoice.unit?.name || 'N/A',
                amount: invoice.totalAmount,
                dueDate: invoice.dueDate
            };

            console.log("🔍 DEBUG - Sending invoice with payload:", payload);

            // Use the correct endpoint format
            const sendUrl = `${sendInvoiceToManager}/${facilityId}`;
            console.log("🔍 DEBUG - Send URL:", sendUrl);

            const response = await makeRequest2(sendUrl, "POST", payload);
            console.log("🔍 DEBUG - Send invoice response:", response);

            if (response.success) {
                toastify("Invoice sent to property manager successfully", "success");
                setShowSendManagerDialog(false);
                setSelectedManager(null);
            } else {
                toastify(response.message || "Failed to send invoice", "error");
            }
        } catch (error) {
            console.error("🔍 DEBUG - Error sending invoice:", error);
            toastify("Failed to send invoice to property manager", "error");
        } finally {
            setSendingInvoice(false);
        }
    };

    const handleResendInvoice = async () => {
        if (!invoice || !invoice.client || !invoice.client.clientId) {
            toastify("Invalid invoice data for resending", "error");
            return;
        }

        try {
            setResending(true);
            toastify("Sending invoice notification...", "info");

            let invoiceUrl = invoice.invoiceUrl || "";
            if (invoiceUrl) {
                invoiceUrl = invoiceUrl.replace("app.payserve.co.ke", "resident.payserve.co.ke");
            }

            const response = await makeRequest2(
                `${resendInvoiceNotification}/${facilityId}`,
                "POST",
                {
                    invoiceId: invoice._id,
                    tenantId: invoice.client.clientId,
                    unitName: invoice.unit.name || "Unit",
                    facilityName: invoice.facility.name,
                    amount: invoice.totalAmount,
                    dueDate: invoice.dueDate,
                    invoiceNumber: invoice.invoiceNumber,
                    invoiceUrl: invoiceUrl
                }
            );

            if (response.success) {
                toastify("Invoice notification sent successfully!", "success");
            } else {
                toastify(response.message || "Failed to send invoice notification", "error");
            }
        } catch (error) {
            console.error("Error sending invoice notification:", error);
            toastify("Failed to send invoice notification. Please try again.", "error");
        } finally {
            setResending(false);
        }
    };

    const actionOptionTemplate = (option) => (
        <div className="dropdown-item-container">
            <i className={option.icon} style={{ marginRight: '10px' }}></i>
            <span>{option.label}</span>
        </div>
    );

    const selectedActionTemplate = (option, props) => {
        return option ? actionOptionTemplate(option) : <span>{props.placeholder}</span>;
    };

    // Custom footer for the dialog with exactly matching button styling from payment modal
    const renderDialogFooter = () => {
        return (
            <div className="d-flex justify-content-end mt-4">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    onClick={() => setShowSendManagerDialog(false)}
                    className="p-button-text btn btn-secondary me-2"
                    disabled={sendingInvoice}
                    type="button"
                />
                <Button
                    label={sendingInvoice ? 'Sending...' : 'Send Invoice'}
                    icon="pi pi-send"
                    onClick={handleSendToManager}
                    disabled={!selectedManager || sendingInvoice}
                    className="btn btn-primary"
                    type="button"
                />
            </div>
        );
    };

    return (
        <>
            <div className={styles.dropdownContainer}>
                <Dropdown
                    value={selectedAction}
                    options={actions}
                    onChange={handleActionChange}
                    placeholder="Invoice Actions"
                    itemTemplate={actionOptionTemplate}
                    valueTemplate={selectedActionTemplate}
                    className="w-full md:w-14rem"
                    disabled={loading || resending || voidingInvoice}
                />

                {(loading || resending || voidingInvoice) && (
                    <div className={styles.loaderIndicator}>
                        <i className="pi pi-spin pi-spinner" style={{ marginLeft: '10px' }}></i>
                    </div>
                )}
            </div>

            {/* Confirm Dialog for Void Invoice */}
            <ConfirmDialog />

            <CashPaymentModal
                visible={showCashPaymentModal}
                onHide={() => setShowCashPaymentModal(false)}
                invoice={invoice}
                facilityId={facilityId}
                onSuccess={onCashPaymentSuccess}
                onError={onCashPaymentError}
            />

            <WhtPaymentModal
                visible={showWhtPaymentModal}
                onHide={() => setShowWhtPaymentModal(false)}
                invoice={invoice}
                facilityId={facilityId}
            // onSuccess={onCashPaymentSuccess}
            // onError={onCashPaymentError}
            />

            <Dialog
                header="Send Invoice to Property Manager"
                visible={showSendManagerDialog}
                style={{ width: '450px' }}
                onHide={() => setShowSendManagerDialog(false)}
                footer={renderDialogFooter()}
                closable={true}
                draggable={false}
                modal={true}
                className="property-manager-dialog"
            >
                {loadingManagers ? (
                    <div className="text-center p-4">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading property managers...</p>
                    </div>
                ) : managers.length > 0 ? (
                    <div className="p-fluid">
                        <div className="field">
                            <label htmlFor="manager">Select Property Manager</label>
                            <Dropdown
                                id="manager"
                                value={selectedManager}
                                options={managers}
                                onChange={(e) => setSelectedManager(e.value)}
                                optionLabel="displayLabel"
                                placeholder="Select a property manager"
                                className="w-full"
                                filter
                                filterBy="displayLabel,email,phoneNumber"
                                emptyFilterMessage="No matching property managers found"
                            />
                        </div>
                        <div className="field mt-3">
                            {selectedManager && (
                                <div className={styles.selectedManagerDetails}>
                                    <p className="m-0"><strong>Email:</strong> {selectedManager.email}</p>
                                    {selectedManager.phoneNumber && (
                                        <p className="m-0 mt-1"><strong>Phone:</strong> {selectedManager.phoneNumber}</p>
                                    )}
                                    <p className="m-0 mt-1"><strong>Type:</strong> {selectedManager.type || selectedManager.role}</p>
                                </div>
                            )}
                        </div>
                        <small className="text-muted">
                            An email will be sent to the selected property manager with the invoice details.
                        </small>
                    </div>
                ) : (
                    <div className="text-center p-4">
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: '#FFA500' }}></i>
                        <p>No property managers found for this facility.</p>

                        {/* Debug information section */}
                        {debugResponse && (
                            <div className="mt-3 text-left" style={{ background: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
                                <details>
                                    <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Debug Response Data</summary>
                                    <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '200px' }}>
                                        {JSON.stringify(debugResponse, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default InvoiceActionsDropdown;